import type { ConsistencyDay } from '@anytracker/core'

import styles from './ConsistencyStrip.module.css'

interface Props {
  /** From core's `consistency`, oldest first. */
  readonly days: readonly ConsistencyDay[]
  /** The same fact in words. Required — see below. */
  readonly caption: string
  /** Bars carry the bands; dots only say logged or not. */
  readonly variant?: 'bars' | 'dots'
}

/**
 * How much was logged each day, lately (T-903).
 *
 * Shared between Home and the post-save confirmation so the two cannot disagree
 * about the same fortnight — the bands come from core and the shape from here,
 * and neither screen owns either.
 *
 * **The caption is not optional.** The strip is `aria-hidden`, because a row of
 * fourteen unlabelled cells is noise to a screen reader and each cell announced
 * separately is worse than silence. That makes the sentence the only accessible
 * form of this information, so it has to exist — and it is a required prop
 * rather than a convention, because a convention would be dropped by whoever
 * next adds a third caller.
 */
export function ConsistencyStrip({ days, caption, variant = 'bars' }: Props) {
  return (
    <div className={`${styles['strip']} ${styles[variant] ?? ''}`}>
      <div className={styles['cells']} aria-hidden="true">
        {days.map((day) => (
          <span
            key={day.dayKey}
            className={styles['cell']}
            data-band={variant === 'dots' ? Math.min(day.band, 1) : day.band}
          />
        ))}
      </div>
      <span className={styles['caption']}>{caption}</span>
    </div>
  )
}
